var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { memo } from 'react';
import { Controller } from 'react-hook-form-v6';
import { VariableSizeList } from 'react-window';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { Autocomplete, autocompleteClasses, TextField, Typography, } from '@mui/material';
import Popper from '@mui/material/Popper';
import { styled } from '@mui/material/styles';
import { ErrorLabel } from '../../components/ErrorLabel/ErrorLabel';
import Tooltip from '../../components/Tooltip';
import 'react-virtualized/styles.css';
const VirtualizedSelect = (props) => {
    var _a, _b;
    const requiredLabel = 'This field is required';
    const isErrorTooltipMessage = props.error && props.errorTooltip;
    const LISTBOX_PADDING = 8;
    const getErrorMessage = () => {
        if (isErrorTooltipMessage && !!props.errorTooltipMessage) {
            return props.errorTooltipMessage;
        }
        else if (isErrorTooltipMessage && props.isRequired) {
            return requiredLabel;
        }
        return '';
    };
    const StyledPopper = styled(Popper)({
        [`& .${autocompleteClasses.listbox}`]: {
            boxSizing: 'border-box',
            '& ul': {
                padding: 0,
                margin: 0,
            },
        },
    });
    const renderRow = (props) => {
        const { data, index, style } = props;
        const dataSet = data[index];
        const inlineStyle = Object.assign(Object.assign({}, style), { top: style.top + LISTBOX_PADDING });
        return (_jsx(Typography, Object.assign({ variant: "body2", component: "li" }, dataSet[0], { style: inlineStyle }, { children: dataSet[1] })));
    };
    const OuterElementContext = React.createContext({});
    const OuterElementType = React.forwardRef((props, ref) => {
        const outerProps = React.useContext(OuterElementContext);
        return _jsx("div", Object.assign({ ref: ref }, props, outerProps));
    });
    const useResetCache = (data) => {
        const ref = React.useRef(null);
        React.useEffect(() => {
            if (ref.current != null) {
                ref.current.resetAfterIndex(0, true);
            }
        }, [data]);
        return ref;
    };
    const ListboxComponent = React.forwardRef(function ListboxComponent(props, ref) {
        const { children } = props, other = __rest(props, ["children"]);
        const itemData = [];
        children.forEach((item) => {
            itemData.push(item);
            itemData.push(...(item.children || []));
        });
        const itemCount = itemData.length;
        const itemSize = 36;
        const getChildSize = (child) => {
            if (child.hasOwnProperty('group')) {
                return 48;
            }
            return itemSize;
        };
        const getHeight = () => {
            if (itemCount > 8) {
                return 8 * itemSize;
            }
            return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
        };
        const gridRef = useResetCache(itemCount);
        return (_jsx("div", Object.assign({ ref: ref }, { children: _jsx(OuterElementContext.Provider, Object.assign({ value: other }, { children: _jsx(VariableSizeList, Object.assign({ itemData: itemData, height: getHeight() + 2 * LISTBOX_PADDING, width: "100%", ref: gridRef, outerElementType: OuterElementType, innerElementType: "ul", itemSize: (index) => getChildSize(itemData[index]), overscanCount: 5, itemCount: itemCount }, { children: renderRow })) })) })));
    });
    return (_jsx("div", { children: _jsx(Tooltip, Object.assign({ title: getErrorMessage() || '' }, { children: _jsxs("div", { children: [_jsx(Controller, { rules: {
                            required: props.isRequired ? 'This field is required' : undefined,
                            validate: props.validate,
                        }, control: props.control, name: props.name, defaultValue: (_a = props.defaultValue) !== null && _a !== void 0 ? _a : null, render: ({ onChange }) => {
                            var _a, _b;
                            return (_jsx(Autocomplete, { value: (_a = props.defaultValue) !== null && _a !== void 0 ? _a : null, options: (_b = props.options) !== null && _b !== void 0 ? _b : [], blurOnSelect: true, selectOnFocus: true, disableListWrap: true, popupIcon: _jsx(ExpandMore, {}), PopperComponent: StyledPopper, getOptionLabel: (option) => option.label || '', ListboxComponent: ListboxComponent, renderInput: (params) => (_jsx(TextField, Object.assign({}, params, { label: props.label }))), renderOption: (props, option) => [props, option.label], onChange: (value, option) => {
                                    onChange(value);
                                    if (props.onChange) {
                                        props.onChange(value, option);
                                    }
                                } }));
                        } }), props.error && !props.errorTooltip && (_jsx(ErrorLabel, { children: (_b = props.customError) !== null && _b !== void 0 ? _b : requiredLabel }))] }) })) }));
};
export default memo(VirtualizedSelect);
